/**
 * Client-side view of the account governance record at
 * `partners/{accountId}/governance/v3Decision`, plus the untrusted-document
 * normalizer. The client NEVER writes this record — it is server-authoritative
 * (v4_submitAccountV3Decision callable + reviewed backfill only).
 */

/** Normalized, still-untrusted view of the stored governance record. */
export interface GovernanceRecordView {
  schemaVersion: number;
  decisionVersion: number;
  /** Kept raw — the fail-closed access resolver owns interpretation. */
  status: string;
  /** Designated Lead Administrator email, lowercased; empty when undesignated. */
  leadAdministratorEmail: string;
}

/**
 * Normalizes a raw Firestore document into a `GovernanceRecordView`.
 * Missing/non-object documents return null (account not in the governance
 * program); malformed fields default rather than throw so downstream
 * resolution stays fail-closed instead of crashing.
 */
export function parseGovernanceRecord(data: unknown): GovernanceRecordView | null {
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const record = data as Record<string, unknown>;
  const lead = (record['leadAdministrator'] as { email?: unknown } | undefined)?.email;
  return {
    schemaVersion: typeof record['schemaVersion'] === 'number' ? record['schemaVersion'] : 0,
    decisionVersion: typeof record['decisionVersion'] === 'number' ? record['decisionVersion'] : 0,
    status: typeof record['status'] === 'string' ? record['status'] : '',
    leadAdministratorEmail: typeof lead === 'string' ? lead.toLowerCase() : '',
  };
}
